import React from "react";
import { CircuitOpenError } from "../services/RestService";

// ── Error deduplication ────────────────────────────────────────────────────
// Prevents a single API outage from sending hundreds of identical alerts.
// Same fingerprint (type + first 100 chars of message) is suppressed for 60 s.
// Max 5 unique reports per 60 s window.

const _seen = new Map<string, number>(); // fingerprint → last-reported ms
const MAX_PER_WINDOW = 5;
let _windowStart = Date.now();
let _windowCount = 0;

function shouldReport(message: string, type: string): boolean {
  // Circuit-open errors are intentional, not real failures — suppress entirely
  if (message.startsWith("API unreachable")) return false;

  const now = Date.now();
  if (now - _windowStart > 60_000) {
    _windowStart = now;
    _windowCount = 0;
    _seen.clear();
  }

  const key = `${type}:${message.slice(0, 100)}`;
  if (_seen.has(key)) return false;
  if (_windowCount >= MAX_PER_WINDOW) return false;

  _seen.set(key, now);
  _windowCount++;
  return true;
}

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
  reported: boolean;
  reportFailed: boolean;
}

interface ErrorPayload {
  message: string;
  stack: string;
  componentStack: string;
  url: string;
  userAgent: string;
  timestamp: string;
  type: "render" | "async" | "unhandled-rejection";
}

// Standalone function — usable outside React tree and in class components
export async function reportErrorToSupport(payload: ErrorPayload): Promise<void> {
  if (!shouldReport(payload.message, payload.type)) return;

  try {
    const adminApiUrl = (import.meta.env.VITE_ADMIN_API_URL as string) ?? "";
    await fetch(`${adminApiUrl}/api/error-report`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      // Fire-and-forget: don't block the UI, but cap at 8s
      signal: AbortSignal.timeout(8000)
    });
  } catch {
    // Never let error reporting cause more errors
  }
}

export class GlobalErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      componentStack: null,
      reported: false,
      reportFailed: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });

    reportErrorToSupport({
      message: error.message ?? "Unknown render error",
      stack: error.stack ?? "",
      componentStack: info.componentStack ?? "",
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      type: "render"
    })
      .then(() => this.setState({ reported: true }))
      .catch(() => this.setState({ reportFailed: true }));
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          reported={this.state.reported}
          reportFailed={this.state.reportFailed}
          onReload={() => window.location.reload()}
          onHome={() => { window.location.href = "/"; }}
        />
      );
    }
    return this.props.children;
  }
}

interface FallbackProps {
  error: Error | null;
  reported: boolean;
  reportFailed: boolean;
  onReload: () => void;
  onHome: () => void;
}

function ErrorFallback({ error, reported, reportFailed, onReload, onHome }: FallbackProps) {
  const reportStatus = reported
    ? "Our support team has been notified automatically."
    : reportFailed
    ? "Please email support@velaro.com if this continues."
    : "Notifying support team...";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.072 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <h1 className="text-xl font-bold text-slate-800 mb-2">
          Something went wrong
        </h1>
        <p className="text-slate-500 text-sm mb-1">
          An unexpected error occurred. {reportStatus}
        </p>
        {reportFailed && (
          <a
            href="mailto:support@velaro.com"
            className="text-blue-600 text-sm underline hover:text-blue-800"
          >
            support@velaro.com
          </a>
        )}

        <div className="flex gap-3 justify-center mt-6">
          <button
            onClick={onReload}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Reload Page
          </button>
          <button
            onClick={onHome}
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
          >
            Go Home
          </button>
        </div>

        {error && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-500 select-none">
              Technical details
            </summary>
            <pre className="mt-2 text-xs text-red-700 bg-red-50 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap break-words">
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ""}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
